import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { Badge } from "./ui/badge";
import { 
  History, 
  Download, 
  Search, 
  Calendar,
  User,
  FileText,
  ArrowUpDown,
  ExternalLink
} from "lucide-react";
import { Alert, AlertDescription } from "./ui/alert";
import { Separator } from "./ui/separator";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuditTrail = ({ calculationId, filename }) => {
  const [auditData, setAuditData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortField, setSortField] = useState("timestamp");
  const [sortDirection, setSortDirection] = useState("desc");
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    if (calculationId) {
      loadAuditTrail();
    }
  }, [calculationId]);

  const loadAuditTrail = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await axios.get(`${API}/calculation/${calculationId}/audit-trail`);
      setAuditData(response.data);
    } catch (e) {
      setError(e.response?.data?.detail || "Error loading audit trail");
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const handleExportAuditReport = async () => {
    if (!auditData || auditData.adjustments.length === 0) {
      alert("No audit data to export");
      return;
    }

    setExporting(true);
    try {
      // Convert audit data to CSV format
      const headers = [
        "Timestamp",
        "Account Number", 
        "Account Name",
        "User ID",
        "Original Pool",
        "New Pool", 
        "Original Bases",
        "New Bases",
        "Comment",
        "Status"
      ];
      
      const csvData = auditData.adjustments.map(adjustment => [
        new Date(adjustment.timestamp).toLocaleString(),
        adjustment.account_number,
        adjustment.account_name,
        adjustment.user_id,
        adjustment.original_pool || "None",
        adjustment.new_pool || "None",
        Array.isArray(adjustment.original_bases) ? adjustment.original_bases.join(", ") : "None",
        Array.isArray(adjustment.new_bases) ? adjustment.new_bases.join(", ") : "None",
        adjustment.user_comment || "No comment",
        adjustment.is_active ? "Active" : "Inactive"
      ]);
      
      // Create CSV content
      const csvContent = [headers, ...csvData]
        .map(row => row.map(cell => `"${cell}"`).join(","))
        .join("\n");
      
      // Create and download file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${filename.replace(/[^a-zA-Z0-9-_ ]/g, '')}_Audit_Trail_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
    } catch (e) {
      setError("Error exporting audit report");
    } finally {
      setExporting(false);
    }
  };

  const filteredAndSortedAdjustments = auditData?.adjustments
    ?.filter(adjustment => {
      if (!searchTerm) return true;
      const searchLower = searchTerm.toLowerCase();
      return (
        adjustment.account_number.toLowerCase().includes(searchLower) ||
        adjustment.account_name.toLowerCase().includes(searchLower) ||
        adjustment.user_id.toLowerCase().includes(searchLower) ||
        (adjustment.user_comment && adjustment.user_comment.toLowerCase().includes(searchLower))
      );
    })
    ?.sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];
      
      if (sortField === "timestamp") {
        aVal = new Date(aVal);
        bVal = new Date(bVal);
      } else {
        aVal = String(aVal || "").toLowerCase();
        bVal = String(bVal || "").toLowerCase();
      }
      
      if (aVal < bVal) return sortDirection === "asc" ? -1 : 1;
      if (aVal > bVal) return sortDirection === "asc" ? 1 : -1;
      return 0;
    }) || [];

  const formatTimestamp = (timestamp) => {
    return new Date(timestamp).toLocaleString();
  };

  const getChangeDescription = (adjustment) => {
    const poolChanged = adjustment.original_pool !== adjustment.new_pool;
    const basesChanged = JSON.stringify(adjustment.original_bases || []) !== JSON.stringify(adjustment.new_bases || []);
    
    let description = [];
    
    if (poolChanged) {
      description.push(`Pool: ${adjustment.original_pool || "None"} → ${adjustment.new_pool || "None"}`);
    }
    
    if (basesChanged) {
      const oldBases = (adjustment.original_bases || []).join(", ") || "None";
      const newBases = (adjustment.new_bases || []).join(", ") || "None";
      description.push(`Bases: ${oldBases} → ${newBases}`);
    }
    
    return description.join(" | ");
  };

  if (loading) {
    return (
      <Card className="shadow-lg">
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <History className="h-5 w-5" />
            <span>Audit Trail</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <span className="ml-2">Loading audit trail...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="shadow-lg">
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <History className="h-5 w-5" />
            <span>Audit Trail</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Alert className="border-red-200 bg-red-50">
            <AlertDescription className="text-red-700">
              {error}
            </AlertDescription>
          </Alert>
          <Button onClick={loadAuditTrail} className="mt-4">
            Retry
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (!auditData) return null;

  return (
    <Card className="shadow-lg">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center space-x-2">
            <History className="h-5 w-5 text-orange-600" />
            <span>Audit Trail</span>
            <Badge variant="secondary">{filename}</Badge>
          </CardTitle>
          
          <div className="flex items-center space-x-4">
            <Badge variant="outline" className="text-sm">
              {auditData.total_adjustments} total adjustments
            </Badge>
            <Button 
              onClick={handleExportAuditReport}
              disabled={exporting || auditData.total_adjustments === 0}
              size="sm"
              className="bg-green-600 hover:bg-green-700"
            >
              <Download className="h-4 w-4 mr-2" />
              {exporting ? "Exporting..." : "Export CSV"}
            </Button>
          </div>
        </div>
      </CardHeader>
      
      <CardContent>
        {auditData.total_adjustments === 0 ? (
          <div className="text-center py-8 text-slate-500">
            <History className="h-16 w-16 mx-auto mb-4 text-slate-300" />
            <h3 className="text-lg font-medium mb-2">No Manual Adjustments Yet</h3>
            <p className="text-sm">
              When you make manual adjustments to account classifications, they will appear here with full audit trail.
            </p>
          </div>
        ) : (
          <>
            {/* Summary Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div className="bg-blue-50 p-4 rounded-lg">
                <div className="flex items-center space-x-2">
                  <FileText className="h-5 w-5 text-blue-600" />
                  <div>
                    <p className="text-sm font-medium text-blue-900">Total Adjustments</p>
                    <p className="text-2xl font-bold text-blue-600">{auditData.total_adjustments}</p>
                  </div>
                </div>
              </div>
              
              <div className="bg-green-50 p-4 rounded-lg">
                <div className="flex items-center space-x-2">
                  <User className="h-5 w-5 text-green-600" />
                  <div>
                    <p className="text-sm font-medium text-green-900">Active Adjustments</p>
                    <p className="text-2xl font-bold text-green-600">
                      {auditData.adjustments.filter(adj => adj.is_active).length}
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="bg-blue-50 p-4 rounded-lg">
                <div className="flex items-center space-x-2">
                  <Calendar className="h-5 w-5 text-blue-600" />
                  <div>
                    <p className="text-sm font-medium text-blue-900">Last Modified</p>
                    <p className="text-sm font-bold text-blue-600">
                      {auditData.adjustments.length > 0 
                        ? formatTimestamp(auditData.adjustments[0].timestamp)
                        : "N/A"
                      }
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <Separator className="mb-6" />

            {/* Search and Filters */}
            <div className="mb-6">
              <div className="relative">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Search by account number, name, user, or comment..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-8"
                />
              </div>
            </div>

            {/* Audit Trail Table */}
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead 
                      className="cursor-pointer hover:bg-slate-50" 
                      onClick={() => handleSort('timestamp')}
                    >
                      Timestamp <ArrowUpDown className="ml-2 h-4 w-4 inline" />
                    </TableHead>
                    <TableHead 
                      className="cursor-pointer hover:bg-slate-50"
                      onClick={() => handleSort('account_number')}
                    >
                      Account <ArrowUpDown className="ml-2 h-4 w-4 inline" />
                    </TableHead>
                    <TableHead>Changes Made</TableHead>
                    <TableHead 
                      className="cursor-pointer hover:bg-slate-50"
                      onClick={() => handleSort('user_id')}
                    >
                      User <ArrowUpDown className="ml-2 h-4 w-4 inline" />
                    </TableHead>
                    <TableHead>Comment</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredAndSortedAdjustments.map((adjustment) => (
                    <TableRow key={adjustment.id} className="hover:bg-slate-50">
                      <TableCell className="font-mono text-sm">
                        {formatTimestamp(adjustment.timestamp)}
                      </TableCell>
                      <TableCell>
                        <div>
                          <div className="font-mono text-sm font-medium">
                            {adjustment.account_number}
                          </div>
                          <div className="text-xs text-slate-500 truncate max-w-40">
                            {adjustment.account_name}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm space-y-1">
                          {getChangeDescription(adjustment)}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-xs">
                          <User className="h-3 w-3 mr-1" />
                          {adjustment.user_id}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm text-slate-600 max-w-48">
                          {adjustment.user_comment ? (
                            <span className="italic">"{adjustment.user_comment}"</span>
                          ) : (
                            <span className="text-slate-400">No comment</span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge 
                          variant={adjustment.is_active ? "default" : "secondary"}
                          className={adjustment.is_active 
                            ? "bg-green-100 text-green-800" 
                            : "bg-gray-100 text-gray-600"
                          }
                        >
                          {adjustment.is_active ? "Active" : "Inactive"}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {filteredAndSortedAdjustments.length === 0 && searchTerm && (
              <div className="text-center py-8 text-slate-500">
                No audit records found matching "{searchTerm}".
              </div>
            )}

            {/* Export Note */}
            <div className="mt-6 p-4 bg-slate-50 rounded-lg">
              <div className="flex items-center space-x-2 text-sm text-slate-600">
                <ExternalLink className="h-4 w-4" />
                <p>
                  <strong>Compliance Note:</strong> This audit trail provides complete traceability 
                  of all manual adjustments for DCAA review. Export the CSV report to include 
                  with your ICE submission documentation.
                </p>
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default AuditTrail;